//
// Copyright (c) 2023 Emplifi, Inc.
//

// @ts-ignore
import i18next from "i18n/i18n";
import {
  ButtonView,
  CKEditorError,
  IconObjectSizeFull,
  IconObjectSizeLarge,
  IconObjectSizeMedium,
  IconObjectSizeSmall,
  Plugin,
} from "ckeditor5";

const RESIZE_ICONS: Record<string, string> = {
  small: IconObjectSizeSmall,
  medium: IconObjectSizeMedium,
  large: IconObjectSizeLarge,
  original: IconObjectSizeFull,
};

export default class ResizeFigureUI extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "ResizeFigureButtons";
  }

  init() {
    const editor = this.editor;
    const options = editor.config.get("figureTools.resize.options");
    const command = editor.commands.get("resizeFigure");
    if (command && Array.isArray(options)) {
      this.bind("isEnabled").to(command);
      for (const option of options) {
        this._registerButton(option);
      }
    }
  }

  _registerButton(option: { label: string; name: string; value: number; icon: string }) {
    const editor = this.editor;
    const { name, value, icon } = option;
    const resizeUnit = editor.config.get("figureTools.resize.unit") as number;
    const optionValueWithUnit = value ? value + resizeUnit : null;
    const command = editor.commands.get("resizeFigure");
    if (!command) {
      return;
    }
    editor.ui.componentFactory.add(name, (locale) => {
      const button = new ButtonView(locale);
      const labelText = this._getOptionLabelValue(option, resizeUnit);
      if (!RESIZE_ICONS[icon]) {
        throw new CKEditorError("resizebuttons-missing-icon", editor, option);
      }
      button.set({
        label: labelText,
        icon: RESIZE_ICONS[icon],
        tooltip: true,
        isToggleable: true,
      });

      button.bind("isEnabled").to(this);
      button.bind("isOn").to(command, "value", getIsOnButtonCallback(optionValueWithUnit));
      this.listenTo(button, "execute", () => {
        editor.execute("resizeFigure", optionValueWithUnit);
      });
      return button;
    });
  }

  private _getOptionLabelValue(option: { label: string; value: number }, resizeUnit: number) {
    if (option.label) {
      return option.label;
    }

    if (option.value) {
      return option.value + resizeUnit;
    } else {
      return i18next.t("ckeditor.resize.original");
    }
  }
}

function getIsOnButtonCallback(value: number | null) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (commandValue: any) => {
    if (value === null && commandValue === value) {
      return true;
    }
    return commandValue !== null && commandValue.maxWidth === value;
  };
}
